/**
 * Pure comparison between the SPA's embedded build version and the server's
 * `client_version` block from /runtime-config.json. See
 * docs/pwa-update-and-offline.md §1.4 for the design.
 *
 * Default semantics (today's JSON, both messages null): the gate is a no-op
 * even when the embedded version differs from `latest`. The operator opts
 * in to a force or soft update by setting one of the message fields, which
 * is the canonical allowlist switch — no SHA ordering needed.
 *
 * Precedence: `force_update_message` wins over `soft_update_message` when
 * both are populated and the version mismatches.
 *
 * Defensive: when `latest` is null/empty (e.g. the deploy hasn't stamped
 * the JSON yet) we treat the build as current regardless of the message
 * fields. This stops a misconfigured deploy from accidentally locking
 * every browser out of the app.
 */

export interface ClientVersion {
  /** Server's notion of the deployed bundle (short Git SHA). May be null
   *  until the deploy workflow stamps the JSON. */
  latest: string | null
  /** Operator copy. When non-null AND the client version mismatches
   *  `latest`, a blocking modal is shown. */
  force_update_message: string | null
  /** Operator copy. When non-null AND the client version mismatches
   *  `latest`, a dismissable banner is shown. Ignored if a force is in
   *  effect (force wins). */
  soft_update_message: string | null
}

export type VersionStatus =
  | { kind: 'current' }
  | {
      kind: 'soft_update'
      current_version: string
      target_version: string
      message: string
    }
  | {
      kind: 'force_update'
      current_version: string
      target_version: string
      message: string
    }

export function compareStatus(
  current_version: string,
  cv: ClientVersion | null,
): VersionStatus {
  // Server hasn't told us anything → assume current.
  if (!cv) return { kind: 'current' }

  // Defensive: server forgot to stamp `latest` (null/empty). Don't
  // gate the user out — treat the unknown target as ok.
  if (!cv.latest) return { kind: 'current' }

  // Versions match — nothing to do regardless of operator messages.
  if (current_version === cv.latest) return { kind: 'current' }

  // Mismatch + force message set → blocking modal. Force always wins
  // over soft so an operator escalation can't be undercut by a stale
  // soft banner copy.
  if (cv.force_update_message) {
    return {
      kind: 'force_update',
      current_version,
      target_version: cv.latest,
      message: cv.force_update_message,
    }
  }

  // Mismatch + soft message set → banner.
  if (cv.soft_update_message) {
    return {
      kind: 'soft_update',
      current_version,
      target_version: cv.latest,
      message: cv.soft_update_message,
    }
  }

  // Mismatch but both messages null → operator hasn't opted into
  // gating. No-op. This is the steady-state today.
  return { kind: 'current' }
}
